/*
 * Copyright (c) 2021, salesforce.com, inc.
 * All rights reserved.
 * SPDX-License-Identifier: BSD-3-Clause
 * For full license text, see the LICENSE file in the repo root or https://opensource.org/licenses/BSD-3-Clause
 */
import React, {useMemo} from 'react'
import PropTypes from 'prop-types'

import {
    Flex,
    HStack,
    List,
    ListItem,
    Text,
    useMediaQuery,
    useMultiStyleConfig
} from '@salesforce/retail-react-app/app/components/shared/ui'
import {styleConfigChakraKeys} from '../../constants'
import Link from '../link'

const LinksList = ({
    links = [],
    heading = '',
    variant,
    color,
    ...otherProps
}) => {
    const [isDesktop] = useMediaQuery('(min-width: 992px)')
    const styles = useMultiStyleConfig(styleConfigChakraKeys.linksList, {
        variant
    })

    return (
        <Flex
            flex={{base: '0 0 calc(50% - 10px)', xl: 1}}
            id="links-list"
            width="100%"
            flexFlow="column"
            gap="16px"
            {...(color ? {color: color} : {})}
            {...otherProps}
        >
            {/* Change check for heading logic */}
            {heading && <Text {...styles.heading}>{heading}</Text>}

            {links && (
                <List spacing={5} {...styles.list}>
                    {variant === 'horizontal' ? (
                        <HStack>
                            {links.map(({sys, fields: link}, ind) => {
                                if (!isDesktop && ind > 1) return

                                return (
                                    <ListItem
                                        key={sys?.id}
                                        {...styles.listItem}
                                        sx={styles.listItemSx}
                                    >
                                        <Link {...link} />
                                    </ListItem>
                                )
                            })}
                        </HStack>
                    ) : (
                        links?.map(({sys, fields: link}) => (
                            <ListItem
                                key={sys?.id}
                                {...styles.listItem}
                                width="fit-content"
                            >
                                <Link
                                    {...link}
                                    buttonStyles={
                                        link.styles ? link.styles : {}
                                    }
                                    url={`${link?.url}`}
                                    isTitleCase={false}
                                />
                            </ListItem>
                        ))
                    )}
                </List>
            )}
        </Flex>
    )
}

LinksList.propTypes = {
    links: PropTypes.arrayOf(
        PropTypes.shape({
            href: PropTypes.string,
            text: PropTypes.string
        })
    ).isRequired,
    heading: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
    variant: PropTypes.oneOf(['vertical', 'horizontal']),
    color: PropTypes.string,
    headingLinkRef: PropTypes.object
}

export default LinksList
